/**
 * The problem-type and priority pickers for a scenario.
 *
 * Problem types come back hierarchically and render in a tree, the same way
 * Troubleshooting's association fields do. The tree emits leaf ids — it only
 * collapses to a parent for display — so what reaches the service is still the
 * flat id list it asks for. Priorities are a flat list through `OWAsyncSelect`.
 *
 * Both go through the async components rather than a bare `read()`: the list
 * endpoints default to ten rows, so client-side filtering would silently hide
 * everything past the tenth. Which means each has to send the search term the
 * way its own endpoint reads it — `name` for both of these.
 *
 * Neither knows about "all". They select ids; `SelectWithAllToggle` wraps them
 * and keeps the all-or-ids choice in its own form field, and the modal turns
 * that pair into the `"all" | number[]` the service wants.
 */

import { Select } from 'antd';
import React from 'react';
import { connect } from 'react-redux';
import OWAsyncSelect from '../ow_async_select/OWAsyncSelect';
import ProblemTypeTreeSelect from '../problem_type_tree_select';
import { nullSafeGet } from '../../utils/DataAccessUtils';
import {
	problemTypesHeirarchicalForBuyer,
	problemTypesRestCrudThunksForBuyer,
} from '../../thunks/problem_types_thunks';
import { workOrderPrioritiesRestCrudThunksForBuyer } from '../../thunks/work_order_priorities_thunks';

const PROBLEM_TYPE_TC = 'aopProblemTypes';
const PRIORITY_TC = 'aopPriorities';

const mapProblemTypeDispatch = (dispatch: any) => ({
	/*
	  The whole signature, the way every other caller passes it — the work-order
	  form and the approval-rule modal both hand the tree select's arguments
	  straight through.

	  Taking only the first two dropped the pagination the component asks with,
	  so `problemTypesHeirarchical` fell back to its own default of 100 where
	  every other screen reads the component's 50. Nothing was unreachable that
	  is reachable elsewhere — the tree filters one loaded page client-side
	  wherever it is used — but this picker was quietly reading a different
	  number of roots from the same control.
	*/
	fetchProblemTypes: (
		params: any,
		targetCollectionName: string,
		pagination: any,
		sorting: any,
		filters: any,
		addToTargetCollection: boolean
	) =>
		dispatch(
			problemTypesHeirarchicalForBuyer(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	fetchMultipleProblemTypes: (ids: number[], targetCollectionName: string) =>
		dispatch(problemTypesRestCrudThunksForBuyer.readMultiple(ids, targetCollectionName)),
});

export const AOPProblemTypeSelect = connect(
	(state: any) => ({ problemTypes: nullSafeGet('problem_types', state) }),
	mapProblemTypeDispatch
)(
	({
		value,
		onChange,
		disabled,
		problemTypes,
		fetchProblemTypes,
		fetchMultipleProblemTypes,
	}: any) => (
		<ProblemTypeTreeSelect
			allowClear
			mode="multiple"
			disabled={disabled}
			placeholder="Select problem types"
			style={{ width: '100%' }}
			value={value ? value : []}
			onChange={onChange}
			stateSlice={problemTypes}
			targetCollectionName={PROBLEM_TYPE_TC}
			fetchData={(
				searchText: string,
				targetCollectionName: string,
				pagination: any,
				sorting: any,
				filters: any,
				addToTargetCollection: boolean
			) =>
				fetchProblemTypes(
					searchText ? { name: searchText } : {},
					targetCollectionName,
					pagination,
					sorting,
					filters,
					addToTargetCollection
				)
			}
			fetchMultiple={fetchMultipleProblemTypes}
			renderRecord={(problemType: any) => (
				<Select.Option key={problemType.id} value={problemType.id}>
					{problemType.name}
				</Select.Option>
			)}
		/>
	)
);

const mapPriorityDispatch = (dispatch: any) => ({
	fetchPriorities: (
		searchText: string,
		targetCollectionName: string,
		pagination: any,
		sorting: any,
		filters: any,
		addToTargetCollection: boolean
	) =>
		dispatch(
			workOrderPrioritiesRestCrudThunksForBuyer.read(
				/*
				  `name`, which is what this endpoint filters on — six other callers
				  pass it, and the problem-type picker above does too. It was
				  `search`, which the endpoint ignores: `OWAsyncSelect` turns off
				  client-side filtering and leaves narrowing to the server, so typing
				  narrowed nothing and a priority past the first page could not be
				  reached at all.
				*/
				searchText ? { name: searchText } : {},
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	fetchMultiplePriorities: (ids: number[], targetCollectionName: string) =>
		dispatch(workOrderPrioritiesRestCrudThunksForBuyer.readMultiple(ids, targetCollectionName)),
});

export const AOPPrioritySelect = connect(
	(state: any) => ({ priorities: nullSafeGet('work_order_priorities', state) }),
	mapPriorityDispatch
)(({ value, onChange, disabled, priorities, fetchPriorities, fetchMultiplePriorities }: any) => (
	<OWAsyncSelect
		mode="multiple"
		allowClear
		disabled={disabled}
		placeholder="Select priorities"
		stateSlice={priorities}
		targetCollectionName={PRIORITY_TC}
		fetchData={fetchPriorities}
		fetchMultiple={fetchMultiplePriorities}
		value={value ? value : []}
		onChange={onChange}
		valueAccessor={(record: any) => record.id}
		renderRecord={(record: any) => (
			<Select.Option key={record.id} value={record.id}>
				{record.name}
			</Select.Option>
		)}
	/>
));
